import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
  BorderStyle,
  AlignmentType,
  HeadingLevel,
  TableLayoutType,
} from 'docx';

const VAT_RATE = 0.2;

const numberFmt = (n) =>
  (Number.isFinite(parseFloat(n)) ? parseFloat(n) : 0).toLocaleString('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/**
 * Teklif verisinden Word (DOCX) belgesi oluşturur.
 * @param {Object} offer - Teklif objesi (company_name, address, offer_data, notes, vb.)
 * @param {Object} companyInfo - Firma bilgileri (company_name, email, phone, address, website)
 * @returns {Document} docx Document instance (Packer.toBlob ile indirilebilir)
 */
export function buildOfferDocx(offer, companyInfo = {}) {
  const companyName = companyInfo.company_name || 'Website Al';
  const companyEmail = companyInfo.email || 'info@website-al.com';
  const companyPhone = companyInfo.phone || '';
  const companyAddress = companyInfo.address || '';
  const companyWebsite = companyInfo.website || '';

  const services = Array.isArray(offer.offer_data?.services) ? offer.offer_data.services : [];
  const packages = Array.isArray(offer.offer_data?.custom_packages) ? offer.offer_data.custom_packages : [];
  const servicesTotal = services.reduce((sum, s) => sum + (parseFloat(s.total) || 0), 0);
  const packagesTotal = packages.reduce((sum, p) => sum + (parseFloat(p.price) || 0), 0);
  const subtotal = servicesTotal + packagesTotal;
  const vatAmount = subtotal * VAT_RATE;
  const total = subtotal + vatAmount;

  const dateStr = new Date().toLocaleDateString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  const children = [];

  // Üst: Firma bilgileri
  children.push(
    new Paragraph({
      children: [new TextRun({ text: companyName, bold: true, size: 22 })],
      spacing: { after: 120 },
    })
  );
  if (companyAddress) {
    children.push(new Paragraph({ text: companyAddress, spacing: { after: 60 } }));
  }
  if (companyEmail) {
    children.push(new Paragraph({ text: `E-posta: ${companyEmail}`, spacing: { after: 60 } }));
  }
  if (companyPhone) {
    children.push(new Paragraph({ text: `Telefon: ${companyPhone}`, spacing: { after: 60 } }));
  }
  children.push(new Paragraph({ text: `Tarih: ${dateStr}`, spacing: { after: 120 } }));
  if (companyWebsite) {
    children.push(new Paragraph({ text: `Web: ${companyWebsite}`, spacing: { after: 200 } }));
  }

  // Müşteri bilgileri
  children.push(
    new Paragraph({
      children: [new TextRun({ text: 'Müşteri Bilgileri', bold: true, size: 24 })],
      heading: HeadingLevel.HEADING_2,
      spacing: { before: 200, after: 120 },
    })
  );
  if (offer.company_name?.trim()) {
    children.push(new Paragraph({ text: `Şirket: ${offer.company_name}`, spacing: { after: 60 } }));
  }
  if (offer.contact_name?.trim()) {
    children.push(new Paragraph({ text: `İletişim: ${offer.contact_name}`, spacing: { after: 60 } }));
  }
  if (offer.email?.trim()) {
    children.push(new Paragraph({ text: `E-posta: ${offer.email}`, spacing: { after: 60 } }));
  }
  if (offer.phone?.trim()) {
    children.push(new Paragraph({ text: `Telefon: ${offer.phone}`, spacing: { after: 60 } }));
  }
  if (offer.address?.trim()) {
    children.push(new Paragraph({ text: `Adres: ${offer.address}`, spacing: { after: 60 } }));
  }
  if (offer.tax_number?.trim()) {
    children.push(new Paragraph({ text: `Vergi No: ${offer.tax_number}`, spacing: { after: 200 } }));
  }

  // Teklif başlığı
  children.push(
    new Paragraph({
      children: [new TextRun({ text: offer.offer_title || 'Teklif', bold: true, size: 28 })],
      heading: HeadingLevel.HEADING_1,
      alignment: AlignmentType.CENTER,
      spacing: { before: 120, after: 200 },
    })
  );

  // Hizmetler tablosu
  if (services.length > 0) {
    children.push(
      new Paragraph({
        children: [new TextRun({ text: 'Hizmetler', bold: true, size: 24 })],
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 120, after: 120 },
      })
    );
    // Hücre kenar boşluğu (Word’te düzgün görünsün diye)
    const cellMargin = { top: 80, bottom: 80, left: 100, right: 100 };
    const makeCell = (content, bold = false) =>
      new TableCell({
        children: [
          new Paragraph(
            bold
              ? { children: [new TextRun({ text: content, bold: true })] }
              : { text: content }
          ),
        ],
        margins: cellMargin,
      });
    const headerRow = new TableRow({
      tableHeader: true,
      children: [
        makeCell('#', true),
        makeCell('Hizmet Adı', true),
        makeCell('Açıklama', true),
        makeCell('Miktar', true),
        makeCell('Birim Fiyat', true),
        makeCell('Toplam', true),
      ],
    });
    const serviceRows = services.map((s, i) =>
      new TableRow({
        children: [
          new TableCell({ children: [new Paragraph({ text: String(i + 1) })], margins: cellMargin }),
          new TableCell({ children: [new Paragraph({ text: s.name || '' })], margins: cellMargin }),
          new TableCell({ children: [new Paragraph({ text: s.description || '' })], margins: cellMargin }),
          new TableCell({ children: [new Paragraph({ text: String(s.quantity) })], margins: cellMargin }),
          new TableCell({ children: [new Paragraph({ text: `${numberFmt(s.unit_price)} TL` })], margins: cellMargin }),
          new TableCell({ children: [new Paragraph({ text: `${numberFmt(s.total)} TL` })], margins: cellMargin }),
        ],
      })
    );
    // Sütun genişlikleri (DXA, 1/20 point; toplam ~11900 A4 genişliği)
    const colWidths = [500, 2500, 3500, 1000, 2200, 2200];
    children.push(
      new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        columnWidths: colWidths,
        layout: TableLayoutType.FIXED,
        rows: [headerRow, ...serviceRows],
        borders: {
          top: { style: BorderStyle.SINGLE, size: 1, color: '000000' },
          bottom: { style: BorderStyle.SINGLE, size: 1, color: '000000' },
          left: { style: BorderStyle.SINGLE, size: 1, color: '000000' },
          right: { style: BorderStyle.SINGLE, size: 1, color: '000000' },
          insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: '000000' },
          insideVertical: { style: BorderStyle.SINGLE, size: 1, color: '000000' },
        },
      })
    );
    children.push(new Paragraph({ text: '', spacing: { after: 200 } }));
  }

  // Özel paketler
  if (packages.length > 0) {
    children.push(
      new Paragraph({
        children: [new TextRun({ text: 'Özel Paketler', bold: true, size: 24 })],
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 120, after: 120 },
      })
    );
    packages.forEach((pkg, idx) => {
      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: `${idx + 1}. ${pkg.name || ''}`, bold: true, size: 22 }),
          ],
          spacing: { after: 60 },
        })
      );
      if (pkg.description) {
        children.push(new Paragraph({ text: pkg.description, spacing: { after: 60 } }));
      }
      if (Array.isArray(pkg.features) && pkg.features.filter(Boolean).length > 0) {
        pkg.features.filter(Boolean).forEach((f) => {
          children.push(new Paragraph({ text: `• ${String(f)}`, spacing: { after: 40 } }));
        });
      }
      children.push(
        new Paragraph({
          children: [new TextRun({ text: `Fiyat: ${numberFmt(pkg.price)} TL`, bold: true })],
          spacing: { after: 120 },
        })
      );
    });
  }

  // Özet
  children.push(
    new Paragraph({
      children: [new TextRun({ text: 'Özet', bold: true, size: 24 })],
      heading: HeadingLevel.HEADING_2,
      spacing: { before: 200, after: 120 },
    })
  );
  children.push(
    new Paragraph({
      text: `Ara Toplam (KDV hariç): ${numberFmt(subtotal)} TL`,
      spacing: { after: 60 },
    })
  );
  children.push(
    new Paragraph({
      text: `KDV (%${(VAT_RATE * 100).toFixed(0)}): ${numberFmt(vatAmount)} TL`,
      spacing: { after: 60 },
    })
  );
  children.push(
    new Paragraph({
      children: [new TextRun({ text: `Genel Toplam (KDV dahil): ${numberFmt(total)} TL`, bold: true, size: 24 })],
      spacing: { after: 200 },
    })
  );

  if (offer.notes?.trim()) {
    children.push(
      new Paragraph({
        children: [new TextRun({ text: 'Notlar:', bold: true, size: 22 })],
        spacing: { before: 200, after: 60 },
      })
    );
    children.push(new Paragraph({ text: offer.notes.trim(), spacing: { after: 120 } }));
  }

  return new Document({
    sections: [
      {
        properties: {},
        children,
      },
    ],
  });
}

/**
 * DOCX belgesini blob olarak üretir (indirme için).
 * @param {Document} doc - buildOfferDocx ile oluşturulmuş Document
 * @returns {Promise<Blob>}
 */
export async function offerDocxToBlob(doc) {
  return Packer.toBlob(doc);
}
